import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import TableBadge from '@spark/ui-components/components/TanStackTable/components/TableBadge';
import VxConsultLogo from './VxConsultLogo';
import { getVxBiometriaCountdown, isVxBiometriaPending } from '../utils/vxBiometriaUtils';
import { formatarDataHoraBr } from '../utils/dataBr';

const getDisplayValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const ProgramacaoHavanVxResultModal = ({
  show,
  result,
  baixandoLaudo,
  onBaixarLaudo,
  onClose
}) => {
  const response = result?.response || {};
  const dadosVx = response?.dados_vx || {};
  const laudoUrl = response?.laudo_url || dadosVx?.laudo;
  const [clockNow, setClockNow] = useState(() => new Date());
  const [showRawDetails, setShowRawDetails] = useState(false);
  const aguardandoBiometria = isVxBiometriaPending(response);
  const biometriaCountdown = useMemo(
    () => getVxBiometriaCountdown(response, clockNow),
    [clockNow, response]
  );

  useEffect(() => {
    if (show) {
      setShowRawDetails(false);
    }
  }, [result, show]);

  useEffect(() => {
    if (!show || !aguardandoBiometria) return undefined;

    const timer = window.setInterval(() => {
      setClockNow(new Date());
    }, 60000);

    return () => window.clearInterval(timer);
  }, [aguardandoBiometria, show]);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <HeaderContent>
          <Modal.Title>Retorno VX</Modal.Title>
          <VxConsultLogo size="sm" />
        </HeaderContent>
      </Modal.Header>
      <Modal.Body>
        <ResultHeader>
          <div>
            <ResultTitle>{response?.mensagem || 'Consulta VX processada.'}</ResultTitle>
            <ResultSubtitle>
              {result?.row?.nome || '-'} | CPF {result?.row?.cpf || '-'}
            </ResultSubtitle>
          </div>
          <TableBadge
            label={response?.acao_executada || response?.status || 'VX'}
            variant={response?.acao_executada === 'laudo-gerado' ? 'success' : 'default'}
          />
        </ResultHeader>

        {aguardandoBiometria && biometriaCountdown ? (
          <BiometriaBox>
            <BiometriaHeader>
              <div>
                <BiometriaLabel>Aguardando Biometria</BiometriaLabel>
                <BiometriaSubtext>Prazo de validação</BiometriaSubtext>
              </div>
              <BiometriaClock $expired={biometriaCountdown.isExpired}>
                {biometriaCountdown.remainingLabel}
              </BiometriaClock>
            </BiometriaHeader>
            <BiometriaDates>
              <span>Pesquisa: {biometriaCountdown.createdAtLabel}</span>
              <span>Expira: {biometriaCountdown.expiresAtLabel}</span>
            </BiometriaDates>
          </BiometriaBox>
        ) : null}

        {laudoUrl ? (
          <LaudoBox>
            <div>
              <LaudoLabel>Laudo disponível</LaudoLabel>
              <LaudoTexto>
                Pesquisa {getDisplayValue(response?.pesquisa_id)} concluída
                {response?.validade ? ` · válida até ${formatarDataHoraBr(response.validade)}` : ''}.
              </LaudoTexto>
            </div>
            <ButtonWithLoading
              variant="primary"
              onClick={() => onBaixarLaudo?.(result?.row)}
              loading={baixandoLaudo}
              loadingText="Baixando..."
              aria-label="Baixar laudo VX"
            >
              Baixar laudo (PDF)
            </ButtonWithLoading>
          </LaudoBox>
        ) : null}
        {!laudoUrl && ['pesquisa-criada', 'pesquisa-em-andamento', 'pesquisa-renovada', 'acompanhamento'].includes(response?.acao_executada) ? (
          <AndamentoBox>
            A pesquisa está em análise na VX. Quando ela for concluída, clique no status da carga na tabela para atualizar e o laudo aparecerá aqui para download.
          </AndamentoBox>
        ) : null}

        <InfoGrid>
          <InfoItem>
            <InfoLabel>Pesquisa</InfoLabel>
            <InfoValue>{getDisplayValue(response?.pesquisa_id)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Consulta</InfoLabel>
            <InfoValue>{getDisplayValue(response?.consulta_id || dadosVx?.consulta)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Status</InfoLabel>
            <InfoValue>{getDisplayValue(response?.status)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Validade</InfoLabel>
            <InfoValue>{getDisplayValue(formatarDataHoraBr(response?.validade || dadosVx?.validade))}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Ação executada</InfoLabel>
            <InfoValue>{getDisplayValue(response?.acao_executada)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Data pesquisa</InfoLabel>
            <InfoValue>{getDisplayValue(biometriaCountdown?.createdAtLabel || formatarDataHoraBr(dadosVx?.data_pesquisa || dadosVx?.data))}</InfoValue>
          </InfoItem>
        </InfoGrid>

        <DetailsToggle
          type="button"
          aria-expanded={showRawDetails}
          onClick={() => setShowRawDetails(current => !current)}
        >
          {showRawDetails ? 'Ocultar detalhes técnicos' : 'Detalhes técnicos'}
        </DetailsToggle>

        {showRawDetails ? (
          <RawSection>
            <RawTitle>Retorno original da VX</RawTitle>
            <RawContent>{JSON.stringify(dadosVx || {}, null, 2)}</RawContent>
          </RawSection>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <ButtonWithLoading variant="outline-secondary" onClick={onClose}>
          Fechar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
  );
};

ProgramacaoHavanVxResultModal.propTypes = {
  show: PropTypes.bool,
  result: PropTypes.shape(),
  baixandoLaudo: PropTypes.bool,
  onBaixarLaudo: PropTypes.func,
  onClose: PropTypes.func.isRequired
};

ProgramacaoHavanVxResultModal.defaultProps = {
  show: false,
  result: null,
  baixandoLaudo: false,
  onBaixarLaudo: undefined
};

export default ProgramacaoHavanVxResultModal;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  padding-right: 16px;
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
`;

const ResultTitle = styled.div`
  color: #163455;
  font-size: 17px;
  font-weight: 700;
`;

const ResultSubtitle = styled.div`
  margin-top: 4px;
  color: #6c757d;
  font-size: 13px;
`;

const BiometriaBox = styled.div`
  margin-bottom: 18px;
  padding: 12px;
  border: 1px solid #f0c36d;
  border-left: 4px solid #f59e0b;
  border-radius: 4px;
  background: #fff8e8;
`;

const BiometriaHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 576px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const BiometriaLabel = styled.div`
  color: #8a5a00;
  font-size: 14px;
  font-weight: 700;
`;

const BiometriaSubtext = styled.div`
  margin-top: 2px;
  color: #6c757d;
  font-size: 12px;
`;

const BiometriaClock = styled.div`
  flex-shrink: 0;
  min-width: 138px;
  padding: 7px 10px;
  border-radius: 4px;
  background: ${({ $expired }) => ($expired ? '#dc3545' : '#163455')};
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
`;

const BiometriaDates = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-top: 10px;
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 600;
`;

const LaudoBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border: 1px solid #b7dfc3;
  border-radius: 6px;
  background: #eef8f1;
`;

const LaudoTexto = styled.div`
  color: #2f5d3a;
  font-size: 13px;
`;

const AndamentoBox = styled.div`
  margin-bottom: 18px;
  padding: 12px 14px;
  border: 1px solid #f0d58c;
  border-radius: 6px;
  background: #fff8e6;
  color: #8a5a00;
  font-size: 13px;
`;

const LaudoLabel = styled.div`
  margin-bottom: 4px;
  color: #163455;
  font-size: 13px;
  font-weight: 700;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  min-width: 0;
  padding: 10px;
  border: 1px solid #e3e9f0;
  border-radius: 4px;
  background: #ffffff;
`;

const InfoLabel = styled.div`
  margin-bottom: 4px;
  color: #6c757d;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

const InfoValue = styled.div`
  color: #24344d;
  font-size: 13px;
  font-weight: 600;
  word-break: break-word;
`;

const DetailsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 14px;
  padding: 4px 8px;
  border: 1px solid #d6dee8;
  border-radius: 4px;
  background: #ffffff;
  color: #6c757d;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;

  &:hover {
    border-color: #aebacc;
    color: #163455;
  }
`;

const RawSection = styled.div`
  margin-top: 8px;
`;

const RawTitle = styled.div`
  margin-bottom: 6px;
  color: #163455;
  font-size: 13px;
  font-weight: 700;
`;

const RawContent = styled.pre`
  max-height: 220px;
  margin: 0;
  padding: 12px;
  border-radius: 4px;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 12px;
  overflow: auto;
`;
